import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Eye, EyeOff, LogIn, Loader2, Clock } from 'lucide-react';
import { toast } from 'sonner';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pendingMsg, setPendingMsg] = useState(null);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Pick up the "awaiting approval" notice from the Signup redirect.
    if (location.state?.pendingApproval) {
      setPendingMsg(location.state.message || 'Your account is awaiting tech approval.');
      if (location.state.email) setEmail(location.state.email);
      // Clear so a refresh doesn't keep showing it.
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      // Trim whitespace from both fields — mobile keyboards often paste a
      // trailing space, which silently breaks login.
      await login(email.trim(), password.trim());
      // Reset scroll BEFORE navigating so the dashboard mounts at top
      // (iOS Safari/Chrome both preserve scroll across SPA navigation).
      try {
        window.scrollTo(0, 0);
        if (document.documentElement) document.documentElement.scrollTop = 0;
        if (document.body) document.body.scrollTop = 0;
      } catch (_) { /* best effort */ }
      // Unified landing: every user lands on the Dashboard regardless of role.
      navigate('/dashboard');
    } catch (error) {
      const msg = error?.message || 'Wrong username or password';
      console.error('[login] failed:', msg);
      // Keep whatever the user typed so they can retry without re-typing.
      // Show a clear, non-enumerating message.
      toast.error(msg, { duration: 5000 });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      {/* Hero Section */}
      <div className="login-hero">
        <div className="relative z-10 p-12 text-white max-w-lg">
          <img 
            src="/pie-logo.png" 
            alt="PIE - Punjab Imports Ecosystem" 
            className="h-24 mb-8"
          />
          <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Inter' }}>
            Punjab Imports Ecosystem
          </h1>
          <p className="text-lg text-white/80">
            Your all-in-one platform for staff operations, reporting, and team collaboration.
          </p>
        </div>
      </div>

      {/* Form Section */}
      <div className="login-form-container">
        <div className="login-form">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <img 
              src="/pie-logo.png" 
              alt="PIE - Punjab Imports Ecosystem" 
              className="h-20 mx-auto mb-4"
            />
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Inter' }}>
                Welcome to PIE
              </h2>
              <p className="text-slate-600 mt-1">Sign in to your account</p>
            </div>

            {pendingMsg && (
              <div
                className="mb-5 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4"
                data-testid="pending-approval-banner"
              >
                <Clock className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
                <div className="text-sm">
                  <p className="font-medium text-amber-900">Account awaiting approval</p>
                  <p className="text-amber-800 mt-1">{pendingMsg}</p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck="false"
                  data-testid="login-email-input"
                  className="focus-visible:ring-[#DC2626]"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  <Link 
                    to="/forgot-password" 
                    className="text-sm text-[#DC2626] hover:text-[#B91C1C]"
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck="false"
                    data-testid="login-password-input"
                    className="pr-10 focus-visible:ring-[#DC2626]"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-[#DC2626] hover:bg-[#B91C1C] text-white"
                disabled={loading}
                data-testid="login-submit-button"
              >
                {loading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <LogIn className="mr-2 h-4 w-4" />
                )}
                Sign In
              </Button>
            </form>

            <div className="mt-6 text-center text-sm text-slate-600">
              Don't have an account?{' '}
              <Link 
                to="/signup" 
                className="text-[#DC2626] hover:text-[#B91C1C] font-medium"
                data-testid="signup-link"
              >
                Sign up
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
